<template>
  <sModal
    data-qa="growthbook-feature-details"
    :title="feature ? feature.id : ''"
    :value="value"
    width="760"
    @close="$emit('close')">
    <div
      v-if="feature"
      class="growthbook-feature-details">
      <section
        class="growthbook-feature-details_block"
        data-qa="growthbook-details-general">
        <div v-if="tags.length"
             class="growthbook-feature-details_line">
          <sChip v-for="tag in tags"
                 :key="tag"
                 background="var(--v-secondary__green_chosen-base)"
                 color="var(--text-color)"
                 :text="tag"/>
        </div>
        <div>
          <KeyValue
            v-if="feature.description"
            :caption="$t('GrowthbookFeatures.Details.Description')"
            data-qa="growthbook-details-description"
            line
            :value="feature.description"/>
          <KeyValue
            :caption="$t('GrowthbookFeatures.Project')"
            data-qa="growthbook-details-project"
            line
            :value="projectName ? projectName : $t('GrowthbookFeatures.NoProject')"/>
          <KeyValue
            v-if="feature.valueType"
            :caption="$t('GrowthbookFeatures.ValueType')"
            data-qa="growthbook-details-value-type"
            line
            :value="feature.valueType || '—'"/>
          <KeyValue
            v-if="feature.defaultValue"
            :caption="$t('GrowthbookFeatures.DefaultValue')"
            data-qa="growthbook-details-default-value"
            line
            :value="displayValue(feature.defaultValue)"/>
          <KeyValue
            :caption="$t('GrowthbookFeatures.Details.Updated')"
            data-qa="growthbook-details-updated"
            line
            :value="formatDate(feature.dateUpdated)"/>
        </div>
        <div
          v-if="feature.archived"
          class="growthbook-feature-details_danger mt-2"
          data-qa="growthbook-details-archived">
          {{ $t('GrowthbookFeatures.Archived') }}
        </div>
      </section>

      <section
        v-for="environment in featureEnvironments"
        :key="environment.id"
        class="growthbook-feature-details_block"
        :data-qa="`growthbook-details-env-${environment.id}`">
        <h4>
          {{ environment.id }}
          <span
            :class="environment.enabled ? 'growthbook-feature-details_success' : 'growthbook-feature-details_danger'"
            data-qa="growthbook-details-env-status">
            · {{ environment.enabled ? $t('GrowthbookFeatures.Details.Enabled') : $t('GrowthbookFeatures.Details.Disabled') }}
          </span>
        </h4>
        <div
          v-if="!environment.enabled"
          class="growthbook-feature-details_hint">
          {{ $t('GrowthbookFeatures.Details.DisabledHint') }}
        </div>
        <div class="growthbook-feature-details_line">
          {{ $t('GrowthbookFeatures.DefaultValue') }}:
          <code>{{ displayValue(environment.defaultValue ?? feature.defaultValue) }}</code>
        </div>
        <div
          v-if="!environment.rules.length"
          class="growthbook-feature-details_hint"
          data-qa="growthbook-details-no-rules">
          {{ $t('GrowthbookFeatures.Details.NoRules') }}
        </div>
        <ol
          v-else
          class="growthbook-feature-details_rules">
          <li
            v-for="rule in environment.rules"
            :key="rule.id"
            :class="{ 'is-disabled': !rule.enabled }"
            :data-qa="`growthbook-details-rule-${rule.id}`">
            <div>
              <b>{{ ruleTypeLabel(rule.type) }}</b>
              <span
                v-if="!rule.enabled"
                class="growthbook-feature-details_danger"
                data-qa="growthbook-details-rule-disabled">
                · {{ $t('GrowthbookFeatures.Details.RuleDisabled') }}
              </span>
            </div>
            <div data-qa="growthbook-details-rule-condition">
              {{ $t('GrowthbookFeatures.Details.If') }}
              <template v-if="conditionParts(rule.condition).length">
                <template v-for="(part, index) in conditionParts(rule.condition)">
                  <span
                    v-if="index"
                    :key="`and-${index}`"> {{ $t('GrowthbookFeatures.Details.And') }} </span>
                  <code :key="`part-${index}`">{{ part }}</code>
                </template>
              </template>
              <template v-else>
                {{ $t('GrowthbookFeatures.Details.Everyone') }}
              </template>
            </div>
            <div
              v-if="rule.value !== null && rule.value !== undefined"
              data-qa="growthbook-details-rule-value">
              {{ $t('GrowthbookFeatures.Details.Serve') }}
              <code>{{ displayValue(rule.value) }}</code>
            </div>
            <div
              v-if="rule.description"
              class="growthbook-feature-details_hint">
              {{ rule.description }}
            </div>
            <details v-if="hasCondition(rule.condition)">
              <summary>{{ $t('GrowthbookFeatures.Details.RawCondition') }}</summary>
              <pre>{{ prettyJson(rule.condition) }}</pre>
            </details>
          </li>
        </ol>
      </section>
    </div>
    <div
      key="footer"
      slot="footer">
      <sButton
        data-qa="growthbook-details-close"
        @click="$emit('close')">
        {{ $t('Common.Close') }}
      </sButton>
      <a
        v-if="feature"
        class="growthbook-feature-details_link"
        data-qa="growthbook-details-open"
        :href="growthbookUrl"
        rel="noopener noreferrer"
        target="_blank">
        {{ $t('GrowthbookFeatures.Details.OpenInGrowthbook') }}
      </a>
    </div>
  </sModal>
</template>

<script>
import KeyValue from 'components/common/KeyValue.vue'

// Оператори умов GrowthBook (MongoDB-подібні), для яких є короткий запис — GrowthbookFeatures.Details.Operators.<оператор без $>
const OPERATORS = [
  '$eq', '$ne', '$gt', '$gte', '$lt', '$lte', '$in', '$nin', '$ini', '$nini', '$regex', '$regexi',
  '$includes', '$notIncludes', '$exists', '$veq', '$vne', '$vgt', '$vgte', '$vlt', '$vlte',
]

const GROWTHBOOK_APP_URL = 'https://app.growthbook.io'

/**
 * Перегляд фічі GrowthBook (лише читання): загальні дані й правила по кожному оточенню
 */
export default {
  name: 'GrowthbookFeatureDetailsModal',
  methods: {
    parseCondition(condition) {
      if (!condition) {
        return {}
      }
      try {
        const parsed = JSON.parse(condition)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch {
        return null
      }
    },
    hasCondition(condition) {
      const parsed = this.parseCondition(condition)
      return parsed === null || Object.keys(parsed).length > 0
    },
    // Умова правила як список частин «атрибут оператор значення» (зʼєднуються «і»).
    // Порожня умова — правило для всіх; непідтримувана форма ($or, вкладені обʼєкти) — сирий JSON
    conditionParts(condition) {
      const parsed = this.parseCondition(condition)
      if (parsed === null) {
        return [condition]
      }
      return Object.entries(parsed).map(([attribute, expected]) => {
        if (attribute.startsWith('$')) {
          return JSON.stringify({ [attribute]: expected })
        }
        if (expected === null || typeof expected !== 'object' || Array.isArray(expected)) {
          return `${attribute} = ${this.formatOperand(expected)}`
        }
        return Object.entries(expected)
          .map(([operator, operand]) => (OPERATORS.includes(operator)
            ? `${attribute} ${this.$t(`GrowthbookFeatures.Details.Operators.${operator.slice(1)}`)} ${this.formatOperand(operand)}`
            : `${attribute}: ${JSON.stringify({ [operator]: operand })}`))
          .join(` ${this.$t('GrowthbookFeatures.Details.And')} `)
      })
    },
    formatOperand(operand) {
      if (Array.isArray(operand)) {
        return `[${operand.map(item => this.formatOperand(item)).join(', ')}]`
      }
      return typeof operand === 'string' ? `"${operand}"` : JSON.stringify(operand)
    },
    prettyJson(value) {
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch {
        return value
      }
    },
    // Значення в GrowthBook — рядок; JSON показуємо компактно, порожнє — як ""
    displayValue(value) {
      if (value === null || value === undefined) {
        return '—'
      }
      return value === '' ? '""' : String(value)
    },
    ruleTypeLabel(type) {
      const key = `GrowthbookFeatures.Details.RuleTypes.${type}`
      return this.$te(key) ? this.$t(key) : type
    },
    formatDate(value) {
      if (!value) {
        return '—'
      }
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString(this.$i18n.locale === 'en' ? 'en-GB' : 'uk-UA')
    },
  },
  computed: {
    tags() {
      return this.feature?.tags || []
    },
    projectName() {
      const project = this.feature?.project
      if (!project) {
        return ''
      }
      return this.projects.find(item => item.id === project)?.name || project
    },
    // Оточення в порядку організації; яких немає у фічі — вимкнені без правил
    featureEnvironments() {
      const featureEnvironments = this.feature?.environments || []
      const ids = this.environments.length
        ? this.environments.map(({ id }) => id)
        : featureEnvironments.map(({ id }) => id)
      return ids.map(id => {
        const environment = featureEnvironments.find(item => item.id === id)
        return {
          id,
          enabled: !!environment?.enabled,
          defaultValue: environment?.defaultValue,
          rules: environment?.rules || [],
        }
      })
    },
    growthbookUrl() {
      return `${GROWTHBOOK_APP_URL}/features/${encodeURIComponent(this.feature?.id || '')}`
    },
  },
  props: {
    /**
     * Видимість модалки
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * Фіча з growthbookAdminFeatures
     */
    feature: {
      type: Object,
      default: null,
    },
    /**
     * Проєкти з growthbookAdminProjects
     */
    projects: {
      type: Array,
      default: () => [],
    },
    /**
     * Оточення організації з growthbookAdminEnvironments (порядок блоків)
     */
    environments: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    KeyValue
  }
}
</script>

<style scoped lang="scss">
.growthbook-feature-details {
  display: grid;
  gap: 12px;
  font-size: 14px;
  line-height: 20px;
  color: var(--text-color);
  &_block {
    border-radius: 10px;
    background-color: var(--v-main__light_green-base);
    h4 {
      margin-bottom: 8px;
      font-weight: 500;
    }
    dl {
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 4px 12px;
      margin: 0;
    }
    dt {
      color: var(--v-secondary__green_not_active-base);
    }
    dd {
      margin: 0;
      word-break: break-word;
    }
  }
  &_line {
    margin-bottom: 6px;
  }
  &_rules {
    margin: 0;
    padding-left: 20px;
    li {
      padding: 6px 0;
      border-bottom: 1px solid var(--v-main__green-base);
      &:last-child {
        border-bottom: none;
      }
      &.is-disabled {
        opacity: 0.6;
      }
    }
    summary {
      cursor: pointer;
      font-size: 12px;
      color: var(--v-secondary__green_not_active-base);
    }
    pre {
      margin: 4px 0 0;
      font-size: 12px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  code {
    padding: 0 4px;
    border-radius: 4px;
    background-color: var(--v-secondary__green_chosen-base);
    word-break: break-word;
  }
  &_hint {
    font-size: 12px;
    color: var(--v-secondary__green_not_active-base);
  }
  &_success {
    color: var(--alert-success-icon);
  }
  &_danger {
    color: var(--text-red, #e53935);
  }
  &_link {
    margin-left: 16px;
  }
}
</style>
